export function buildGlobalSearchScopeKey(companyId, userId) {
  return JSON.stringify([companyId ?? null, userId ?? null]);
}

export function globalSearchRecentStorageKey(companyId, userId) {
  if (!companyId || !userId) return null;
  return `global_search_recent:${buildGlobalSearchScopeKey(companyId, userId)}`;
}

export function createGlobalSearchLifecycle() {
  let active = null;
  let scopeKey = null;
  const cancel = () => {
    active?.controller.abort();
    active = null;
  };
  return {
    cancel,
    syncScope(nextScopeKey) {
      if (scopeKey !== nextScopeKey) cancel();
      scopeKey = nextScopeKey;
    },
    isScopeCurrent(key) {
      return scopeKey === key;
    },
    begin(query) {
      cancel();
      const request = { query: String(query || '').trim(), scopeKey, controller: new AbortController() };
      active = request;
      return {
        query: request.query,
        signal: request.controller.signal,
        isCurrent: () => active === request && scopeKey === request.scopeKey && !request.controller.signal.aborted,
      };
    },
  };
}

/** Each started wave has rejection handling immediately, even if another wave fails first. */
export async function runGlobalSearchWaves({ request, get, url, onWave, onProgress }) {
  const failures = [];
  let succeeded = 0;
  const runWave = async (label, params) => {
    if (!request.isCurrent()) return null;
    onProgress?.(label);
    try {
      const response = await get(url, {
        params: { q: request.query, limit: 40, ...params },
        signal: request.signal,
        cacheTtlMs: 0,
        // A new attempt owns its transport; a just-aborted promise cannot be reused.
        noDedup: true,
      });
      if (!request.isCurrent()) return null;
      if (!response.data?.success) throw Object.assign(new Error('search_wave_failed'), { response });
      const data = response.data.data || {};
      // Fast deliberately advertises a continuing search. Deep source errors are
      // real partial results even when the HTTP envelope reports success.
      if (data.search_errors?.length || (params.phase !== 'fast' && data.search_incomplete === true)) {
        failures.push({ label, error: new Error('search_sources_incomplete') });
      }
      succeeded += 1;
      onWave?.(data);
      return data;
    } catch (error) {
      if (request.isCurrent()) failures.push({ label, error });
      return null;
    }
  };

  const fast = runWave('быстрый поиск…', { phase: 'fast' });
  const supplier = runWave('товары поставщиков…', { phase: 'deep', tables: 'supplier_products' });
  const fastData = await fast;
  if (!request.isCurrent()) return { status: 'canceled', failures };
  await Promise.all([
    supplier,
    !fastData?.our_products?.length ? runWave('наши товары…', { phase: 'deep', tables: 'products' }) : Promise.resolve(null),
    runWave('категории…', { phase: 'deep', tables: 'categories' }),
    runWave('заказы и склады…', { phase: 'deep', tables: 'orders,documents,warehouses' }),
  ]);
  if (!request.isCurrent()) return { status: 'canceled', failures };
  return { status: failures.length ? (succeeded ? 'partial' : 'failed') : 'completed', failures };
}
